import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ⚠️ IMPORTANTE:
// - device_id  → PERSISTENTE en localStorage. Sobrevive cerrar/abrir el navegador,
//                cambio de pestaña, refresh. Un mismo dispositivo cuenta 1 vez en analytics.
// - session_id → SESIÓN de tab (sessionStorage). Se usa para "activos ahora".
const DEVICE_KEY = 'am_device_id';
const SESSION_KEY = 'am_session_id';
const HEARTBEAT_MS = 60 * 1000; // 1 min

function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      // uuid corto y estable
      id = `d_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch (e) {
    // navegador con localStorage bloqueado → volvemos a un id volátil pero al menos
    // coherente durante la sesión
    return `d_nostore_${Date.now()}`;
  }
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    return `s_${Date.now()}`;
  }
}

/**
 * Visitor tracker: pings the backend on every route change AND every minute
 * while the tab is open, so the admin analytics can compute "active now"
 * (sessions seen in last 5 minutes) reliably.
 *
 * IMPORTANTE: enviamos device_id (persistente) para que las estadísticas
 * cuenten dispositivos únicos, no cada refresh/heartbeat.
 */
export const VisitorTracker = () => {
  const location = useLocation();
  const heartbeatRef = useRef(null);

  useEffect(() => {
    const deviceId = getDeviceId();
    const sid = getSessionId();
    const send = () => {
      // Don't track admin / API routes themselves
      if (location.pathname.startsWith('/admin')) return;
      axios
        .post(
          `${API_URL}/api/track-visit`,
          {
            session_id: sid,
            device_id: deviceId,
            path: location.pathname || '/',
            referrer: document.referrer || '',
          },
          { withCredentials: false }
        )
        .catch(() => {
          /* silent — analytics must never break the app */
        });
    };
    send();

    // Restart heartbeat for the current path
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(send, HEARTBEAT_MS);
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [location.pathname]);

  return null;
};
